import {AuthAgent} from 'quickstart/types'
import {deepMerge} from 'tizra'
import * as A from '../admin'
import {blockMeta} from '../meta'
import {PartialDeep} from 'type-fest'

export interface SignInBlockConfig extends A.BlockConfig {
  authAgent: AuthAgent | ''
  heading: string
  message: string
  autoFocus: boolean
}

const defaultConfig: SignInBlockConfig = {
  authAgent: '',
  heading: 'Sign In',
  message: '',
  autoFocus: true,
  FREEMARKER: {
    message: 'markdown',
  },
}

const migrate: A.Migrate<SignInBlockConfig, any> = ({config}) => {
  // In block context, interpret authAgent=tizra as internal, in other words
  // don't check for customLoginUrl, since this used to be the common
  // configuration for sites that wanted a Tizra auth backdoor.
  // https://github.com/Tizra/cubchicken/issues/1036
  if (config?.authAgent === 'tizra') {
    config = {...config, authAgent: 'internal'}
  }
  return deepMerge(defaultConfig)(config)
}

export interface SignInBlockGlobalConfig extends A.GlobalBlockConfig {
  authAgent: AuthAgent
  modal: {enabled: boolean}
  passwordReset: {enabled: boolean; url: string}
  registration: {enabled: boolean; url: string}
  signin: {url: string}
}

const defaultGlobalConfig: SignInBlockGlobalConfig = {
  // Don't change this to 'internal' here because it will break existing sites
  // that have never saved this config. Change it instead on the pattern site.
  authAgent: 'custom',
  modal: {
    enabled: true,
  },
  passwordReset: {
    enabled: true,
    url: '/~passwordHelp',
  },
  registration: {
    enabled: true,
    url: '/~registerUser',
  },
  signin: {
    url: '/signin',
  },
}

const globalMigrate: A.GlobalMigrate<SignInBlockGlobalConfig> = ({
  globalConfig: unknownGlobalConfig,
}) => {
  let globalConfig = {
    ...(unknownGlobalConfig as any),
  } as PartialDeep<SignInBlockGlobalConfig>

  // The defaultGlobalConfig originally had signin.url="/login" which is
  // a reserved path in Tizra. If we find that in the config, change it to the
  // new default.
  if (globalConfig.signin?.url === '/login') {
    globalConfig.signin = {
      ...globalConfig.signin,
      url: defaultGlobalConfig.signin.url,
    }
  }

  // In global context, interpret authAgent=tizra as custom, in other words
  // check for customLoginUrl before falling back to Tizra auth.
  // @ts-expect-error invalid authAgent
  if (globalConfig.authAgent === 'tizra') {
    globalConfig.authAgent = 'custom'
  }

  return deepMerge(defaultGlobalConfig)(globalConfig)
}

export const meta = blockMeta({
  name: 'signin',
  displayName: 'SignInBlock',
  title: 'Sign In',
  defaultConfig,
  migrate,
  defaultGlobalConfig,
  globalMigrate,
})
